import { Button, Loader } from '@storybook';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { pdfAllCombined, IPdfDownloadOption } from './constants';
import './pdf-download-modal.scss';

interface IPdfDownloadModal {
	visible: boolean;
	handleClose: () => void;
	onDownload: (selectedOptions: string[]) => Promise<void> | void;
	title?: string;
	options: IPdfDownloadOption[];
	loading?: boolean;
}

interface ICheckboxStates {
	[key: string]: boolean;
}

interface ITarget {
	id: string;
	checked: boolean;
}

interface IEvent {
	target: ITarget;
}

export const PdfDownloadModal: FC<IPdfDownloadModal> = ({
	visible,
	handleClose,
	onDownload,
	title = "Download Options",
	options,
	loading = false,
}) => {
	// Local states
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [checkboxStates, setCheckboxStates] = useState<ICheckboxStates>({});
	const [allCombined, setAllCombined] = useState<boolean>(false);

	// Initialize checkbox states
	useEffect(() => {
		const initialStates: ICheckboxStates = {};
		options.forEach(option => {
			initialStates[option.id] = false;
		});
		setCheckboxStates(initialStates);
	}, [options]);

	const resetState = useCallback(() => {
		const resetStates: ICheckboxStates = {};
		options.forEach(option => {
			resetStates[option.id] = false;
		});
		setCheckboxStates(resetStates);
		setAllCombined(false);
	}, [options]);

	useEffect(() => {
		if (!visible) {
			resetState();
		}
	}, [visible, resetState]);

	const renderLabel = useMemo(() => {
		if (isLoading || loading) {
			return <Loader type="loader" dimension={20} className="loader-white" />;
		}
		return 'Download';
	}, [isLoading, loading]);

	// Check if all individual options are selected to auto-check "All combined"
	useEffect(() => {
		const allSelected = options.every(option => checkboxStates[option.id]);
		if (allSelected && options.length > 0) {
			setAllCombined(true);
		}
	}, [checkboxStates, options]);

	const handleCheckBoxChange = useCallback(
		(event: IEvent) => {
			const { id, checked } = event.target;
			
			if (id === pdfAllCombined.id) {
				setAllCombined(checked);
				// When "All combined" is selected, deselect individual options
				if (checked) {
					const newStates: ICheckboxStates = {};
					options.forEach(option => {
						newStates[option.id] = false;
					});
					setCheckboxStates(newStates);
				}
			} else {
				// When individual option is selected, deselect "All combined"
				setAllCombined(false);
				setCheckboxStates(prev => ({
					...prev,
					[id]: checked
				}));
			}
		},
		[options]
	);

	const isDisabled = useMemo(() => {
		const anyIndividualSelected = Object.values(checkboxStates).some(Boolean);
		return !anyIndividualSelected && !allCombined;
	}, [checkboxStates, allCombined]);

	const handleDownload = useCallback(async () => {
		if (isLoading || loading) return;

		setIsLoading(true);
		try {
			const selectedOptions: string[] = [];
			
			if (allCombined) {
				selectedOptions.push(pdfAllCombined.key);
			} else {
				options.forEach(option => {
					if (checkboxStates[option.id]) {
						selectedOptions.push(option.key);
					}
				});
			}

			await onDownload(selectedOptions);
			handleClose();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Download error:', error);
		} finally {
			setIsLoading(false);
		}
	}, [allCombined, checkboxStates, options, onDownload, handleClose, isLoading, loading]);

	if (!visible) {
		return null;
	}

	return (
		<div className="pdf-download-modal__wrapper">
			<div className="pdf-download-modal__container">
				<div className="pdf-download-modal__header">
                    <div>
                        <h3 className="pdf-download-modal__title">{title}</h3>
                        <div className="pdf-download-modal__subtitle">
                            Choose option that best suits your needs:
                        </div>
                    </div>
					<div>
						<i
							className="pdf-download-modal__close-icon ri-close-line"
							onClick={handleClose}
						/>
					</div>
				</div>
				
				<div className="pdf-download-modal__content">
					<div className="pdf-download-modal__options-box">
						{options.map((option) => (
							<div
								key={option.id}
								className={`pdf-download-modal__checkbox-wrapper ${
									allCombined ? 'pdf-download-modal__checkbox-wrapper--disabled' : ''
								}`}
							>
								<input
									type="checkbox"
									id={option.id}
									checked={checkboxStates[option.id] || false}
									onChange={handleCheckBoxChange}
									disabled={allCombined}
									className="pdf-download-modal__checkbox"
								/>
								<div
									className="pdf-download-modal__checkbox-container"
									onClick={() => {
										if (!allCombined) {
											const event: IEvent = {
												target: {
													id: option.id,
													checked: !checkboxStates[option.id],
												},
											};
											handleCheckBoxChange(event);
										}
									}}
								>
									<label className="pdf-download-modal__checkbox-label">
										{option.label}
									</label>
								</div>
							</div>
						))}
					</div>

					<div className="pdf-download-modal__combined-option">
						<input
							type="checkbox"
							id={pdfAllCombined.id}
							checked={allCombined}
							onChange={handleCheckBoxChange}
							className="pdf-download-modal__checkbox"
						/>
						<label
							className="pdf-download-modal__checkbox-label"
							onClick={() => {
								const event: IEvent = {
									target: {
										id: pdfAllCombined.id,
										checked: !allCombined,
									},
								};
								handleCheckBoxChange(event);
							}}
						>
							{pdfAllCombined.label}
						</label>
					</div>
				</div>
				
				<div className="pdf-download-modal__footer">
					<Button
						type="button__outline--secondary"
						label="Cancel"
						handleClick={handleClose}
					/>
					<Button
						type="button__filled--primary"
						label={renderLabel}
						handleClick={handleDownload}
						disabled={isDisabled}
					/>
				</div>
			</div>
		</div>
	);
};
